import ContentContainer from "@src/components/ContentContainer";
import PageTitle from "@src/components/PageTitle";
import PageTopSection, {
  TopBarColumn,
  TopBarSection,
} from "@src/components/PageTopSection/PageTopSection";
import { SearchInput } from "@src/components/SearchInput";
import WrapperContainer from "@src/components/WrapperContainer";
import { theme } from "@src/theme";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@src/hooks/useDebounce";
import styled from "styled-components";
import { VinylCard, VinylCardSelected } from "./components/VinylCard";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios, { AxiosResponse } from "axios";
import { SubmitButton } from "@src/components/SubmitButton";
import { IVinyl } from "@src/types/vinyl";
import UIModal from "@src/components/UIModal/UIModal";
import useModal from "@src/hooks/useModal";
import { UIButton } from "@src/components/UIButton";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import VinylSkeleton from "./components/VinylSkeleton";
import httpApi from "@src/utils/httpApi";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import { setShopCurator } from "@src/redux/slices/authSlice";
import { useDispatch } from "react-redux";
import {
  IShopCurator,
  emptyInitialStateShopCurator,
} from "@src/types/shopCurator";
import { useAppSelector } from "@src/redux/hooks";
import { IShopRecord } from "@src/types/shopRecord";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@src/context/NotificationContext";
import { FEATURES } from "@src/constants";
import { getOdooId, refreshSavedPick, resolveSavedPick } from "./vinylRecords";

const REQUIRED_VINYLS_COUNT = 5;
const EXTRA_VINYLS_COUNT = 2;

/** The five picks that make up the curator's top 5. */
const TOP_SLOT_KEYS = Array.from(
  { length: REQUIRED_VINYLS_COUNT },
  (_, i) => `slot${i + 1}`
);
/**
 * The two optional picks shown to the right of the top 5 [ROVR-4187]. They are
 * submitted as `shop-record` rows flagged `extra: true` and never count towards
 * the five required picks.
 */
const EXTRA_SLOT_KEYS = Array.from(
  { length: EXTRA_VINYLS_COUNT },
  (_, i) => `extra${i + 1}`
);

const extrasEnabled = FEATURES.VINYL_EXTRA_SELECTIONS_ENABLED;
const selectableSlotKeys = extrasEnabled
  ? [...TOP_SLOT_KEYS, ...EXTRA_SLOT_KEYS]
  : TOP_SLOT_KEYS;

const emptySlots = (): Record<string, Partial<IVinyl> | null> =>
  selectableSlotKeys.reduce(
    (acc, slotKey) => ({ ...acc, [slotKey]: null }),
    {} as Record<string, Partial<IVinyl> | null>
  );

/** Records that make up the top 5 — an unset `extra` reads as a top-5 pick. */
const topRecordsOf = (records: IShopRecord[] = []) =>
  records.filter((record) => record?.extra !== true);
const extraRecordsOf = (records: IShopRecord[] = []) =>
  records.filter((record) => record?.extra === true);

interface VinylPageProps {}
const VinylPage: React.FC<VinylPageProps> = () => {
  const role = useRole();
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const { addNotification } = useNotification();
  const { shopCurator } = useAppSelector((state) => state.auth);
  const { documentId: myCuratorDocumentId, vinylListEnabled } =
    useAuthCurator();
  const [submitted, setSubmitted] = useState(false);
  const [isReadyToSubmit, setIsReadyToSubmit] = useState(false);
  const {
    isOpen: reachLimitAlertIsOpen,
    closeModal: closeReachLimitAlert,
    openModal: openReachLimitAlert,
  } = useModal();
  const [selectedVinyls, setSelectedVinyls] =
    useState<Record<string, Partial<IVinyl> | null>>(emptySlots);

  const [filters, setFilters] = useState({
    searchText: "",
  });
  const debouncedSearchText = useDebounce(filters.searchText, 800);

  const { data: selectedVinylsResponse, isFetching: isFetchingSelected } =
    useQuery({
      queryKey: [QUERY_KEYS.GET_SELECTED_VINYLS],
      queryFn: async () => {
        return axios.get(
          `${process.env.REACT_APP_GET_SELECTED_VINYLS_API_URL}`
        );
      },
    });

  const {
    data: vinyls,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_VINYLS, debouncedSearchText],
    queryFn: async ({ pageParam = 1 }) => {
      const url = new URL(process.env.REACT_APP_GET_VINYL_API_URL || "");
      url.searchParams.append("page", String(pageParam));
      url.searchParams.append("pageSize", "50");
      if (debouncedSearchText) {
        url.searchParams.append("search", debouncedSearchText);
      }
      return axios.get(url.toString());
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.meta?.pagination;
      if (pagination?.has_next) {
        return pagination.page + 1;
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 15,
  });

  const onSelectVinyl = (vinyl: Partial<IVinyl> | null) => {
    // A submitted selection is locked — extras included. Without this, a
    // curator whose top 5 is already in could drop a pick into an empty extra
    // slot that they could then neither remove nor submit.
    if (submitted) {
      addNotification(
        "Your selection has already been submitted and can't be changed.",
        "info"
      );
      return;
    }
    // Fill the top 5 first, then spill into the extra slots. The limit alert
    // only fires once every slot on offer is taken.
    const emptySlot = selectableSlotKeys.find(
      (slot) => selectedVinyls[slot] === null
    );
    if (!emptySlot) {
      openReachLimitAlert();
      return;
    }
    setSelectedVinyls((prev) => ({ ...prev, [emptySlot]: vinyl }));
  };
  const checkIfReadyToSubmit = () => {
    // Extras are optional, so only the top 5 gates the submit button.
    const selectedCount = TOP_SLOT_KEYS.filter(
      (slot) => selectedVinyls[slot] !== null
    )?.length;
    setIsReadyToSubmit(selectedCount === REQUIRED_VINYLS_COUNT);
    if (selectedCount < REQUIRED_VINYLS_COUNT) {
      setSubmitted(false);
    }
  };
  useEffect(() => {
    checkIfReadyToSubmit();
  }, [selectedVinyls]);
  const onRemoveVinyl = (slot: string) => {
    setSelectedVinyls((prev) => ({ ...prev, [slot]: null }));
  };

  const fetchShopCurator = () => {
    if (!myCuratorDocumentId) return;
    httpApi
      .get(`api/shop-curators`, {
        params: {
          filters: {
            curator: {
              documentId: {
                $eq: myCuratorDocumentId,
              },
            },
          },
          populate: {
            shopRecords: {
              fields: ["*"],
            },
          },
        },
      })
      .then(({ data }: AxiosResponse<{ data: IShopCurator[] }>) => {
        if (!data || data?.data?.length === 0) {
          dispatch(setShopCurator(emptyInitialStateShopCurator));
          return;
        }
        dispatch(setShopCurator(data.data[0]));
        // Mark as submitted only if they have exactly 5 top-5 records
        if (
          topRecordsOf(data?.data?.[0]?.shopRecords).length ===
          REQUIRED_VINYLS_COUNT
        ) {
          setSubmitted(true);
        }
      });
  };
  const vinylsData = useMemo(
    () => (vinyls?.pages ? vinyls.pages.flatMap((page) => page.data.data) : []),
    [vinyls]
  );

  const loadVinylsFromShopCurator = useCallback(() => {
    // Don't load if user has already made selections (unless submitted)
    if (!submitted) {
      const hasUserSelection = Object.values(selectedVinyls).some(
        (v) => v && v.id
      );
      if (hasUserSelection) return;
    }

    // No shop curator or records to load
    if (!shopCurator || shopCurator?.shopRecords?.length === 0) return;

    const mapRecord = (record: IShopRecord): Partial<IVinyl> | null => {
      if (!record || typeof record !== "object" || !("id" in record)) {
        return null;
      }

      return resolveSavedPick(record, selectedVinylsResponse?.data?.data);
    };

    // The relation is unordered, so the `extra` flag — not the position — is
    // what separates the top 5 from the extras.
    const mappedTopRecords = topRecordsOf(shopCurator.shopRecords).map(
      mapRecord
    );
    const mappedExtraRecords = extrasEnabled
      ? extraRecordsOf(shopCurator.shopRecords).map(mapRecord)
      : [];

    // Fill slots with available records, ensure we have exactly 5 top slots
    // (plus the 2 extra slots when they are enabled)
    const slotsToFill = [
      ...TOP_SLOT_KEYS.map((slotKey, i) => ({
        slotKey,
        vinyl: mappedTopRecords[i] || null,
      })),
      ...(extrasEnabled
        ? EXTRA_SLOT_KEYS.map((slotKey, i) => ({
            slotKey,
            vinyl: mappedExtraRecords[i] || null,
          }))
        : []),
    ];

    setSelectedVinyls(
      slotsToFill.reduce(
        (acc, { slotKey, vinyl }) => ({
          ...acc,
          [slotKey]: vinyl,
        }),
        {} as Record<string, Partial<IVinyl> | null>
      )
    );
  }, [shopCurator, selectedVinyls, submitted, selectedVinylsResponse]);

  useEffect(() => {
    fetchShopCurator();
  }, [myCuratorDocumentId]);

  // Load the saved selection whenever it changes. Deliberately not keyed on the
  // vinyl grid's pages: every page the infinite scroll fetched used to re-run
  // this and overwrite the slots with the saved records, wiping picks made
  // since.
  useEffect(() => {
    if (!shopCurator || shopCurator?.shopRecords?.length === 0) return;
    loadVinylsFromShopCurator();
  }, [shopCurator]);

  // The saved selection usually loads before the curator-selection catalogue
  // does, so restored picks are resolved again once it lands — that's where a
  // pick saved before ROVR-4188 gets its cover. Picks the curator made are
  // left alone.
  useEffect(() => {
    const catalogue: IVinyl[] = selectedVinylsResponse?.data?.data ?? [];
    if (catalogue.length === 0) return;
    setSelectedVinyls((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(prev).forEach(([slot, vinyl]) => {
        const refreshed = refreshSavedPick(vinyl, catalogue);
        if (refreshed === vinyl) return;
        next[slot] = refreshed;
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [selectedVinylsResponse, selectedVinyls]);

  const handleSubmit = () => {
    if (!vinylListEnabled) return;
    if (!isReadyToSubmit || isLoading || isFetchingNextPage || submitted)
      return;

    const pickedIn = (slotKeys: string[]) =>
      slotKeys
        .map((slot) => selectedVinyls[slot])
        .filter((v) => v?.id !== null && v?.id !== undefined);

    const vinylsToSubmit = pickedIn(TOP_SLOT_KEYS);
    // Extras are optional and never counted towards the required 5.
    const extraVinylsToSubmit = extrasEnabled ? pickedIn(EXTRA_SLOT_KEYS) : [];

    // Validate we have exactly 5 vinyls before submitting
    if (vinylsToSubmit.length !== REQUIRED_VINYLS_COUNT) {
      addNotification(
        `Cannot submit. You must select exactly ${REQUIRED_VINYLS_COUNT} vinyls. Currently selected: ${vinylsToSubmit.length}`,
        "error"
      );

      // Log validation error to Datadog
      if (window.DD_LOGS) {
        window.DD_LOGS.logger.warn("Vinyl selection validation failed", {
          curatorId: myCuratorDocumentId,
          expectedCount: REQUIRED_VINYLS_COUNT,
          actualCount: vinylsToSubmit.length,
          context: "vinyl_selection_submit",
        });
      }

      setSubmitted(false);
      return;
    }

    setSubmitted(true);

    const toRecord = (v: Partial<IVinyl> | null, extra: boolean) => ({
      year: v?.year ? Number(v.year) : undefined,
      label: typeof v?.label === "boolean" ? "" : String(v?.label ?? ""),
      artist: typeof v?.artist === "boolean" ? "" : String(v?.artist ?? ""),
      name: typeof v?.name === "boolean" ? "" : String(v?.name ?? ""),
      description:
        typeof v?.description_demo === "boolean"
          ? ""
          : String(v?.description_demo ?? ""),
      quantity: v?.qty_available ? Number(v.qty_available) : 1,
      documentId: v?.product_tmpl_id ? String(v.product_tmpl_id[0]) : "",
      id: v?.id ? Number(v.id) : undefined,
      // Sent explicitly, null included, so the backend never falls back to
      // `id` for a pick whose `id` is a Strapi PK.
      odooId: getOdooId(v),
      // Always use x_studio_discogs_image_link as it's normalized
      imageUrl: v?.x_studio_discogs_image_link
        ? String(v.x_studio_discogs_image_link)
        : "",
      // Only send the flag when the feature is on, so a flag-off submit keeps
      // sending exactly the payload the current backend accepts.
      ...(extrasEnabled ? { extra } : {}),
    });

    const records = [
      ...vinylsToSubmit.map((v) => toRecord(v, false)),
      ...extraVinylsToSubmit.map((v) => toRecord(v, true)),
    ] as unknown as IShopRecord[];

    // Log submission attempt
    if (window.DD_LOGS) {
      window.DD_LOGS.logger.info("Vinyl selection submit started", {
        curatorId: myCuratorDocumentId,
        vinylCount: records.length,
        vinylIds: records.map((r) => r.id),
        context: "vinyl_selection_submit",
      });
    }

    httpApi
      .post(`api/shop-curators/${myCuratorDocumentId}/vinyl-records`, {
        data: {
          records,
          bin: {
            bin: "bin1",
            title: "Rovr Soho Curator Picks",
          },
        },
      })
      .then((response: AxiosResponse) => {
        const { data } = response as AxiosResponse<{ data: IShopCurator }>;
        const shopCuratorUpdated: IShopCurator = data.data;

        // Log successful submission to Datadog
        if (window.DD_LOGS) {
          window.DD_LOGS.logger.info("Vinyl selection submitted successfully", {
            curatorId: myCuratorDocumentId,
            vinylCount: records.length,
            vinylIds: records.map((r) => r.id),
            shopCuratorId: shopCuratorUpdated.documentId,
            shopRecordsCount: shopCuratorUpdated.shopRecords?.length || 0,
            context: "vinyl_selection_submit",
            timestamp: new Date().toISOString(),
          });
        }

        if (data.data && "shopRecords" in data.data) {
          records.forEach((record) => {
            // Only with a real Odoo id: a Strapi PK here would move an
            // unrelated vinyl.
            if (record.odooId) {
              axios.get(
                `${process.env.REACT_APP_MOVE_VINYLS_CURATOR_SELECTION_API_URL}${record.odooId}`
              );
            }
          });
          dispatch(setShopCurator(shopCuratorUpdated));
        }
      })
      .catch((error) => {
        // Log error to Datadog
        if (window.DD_LOGS) {
          window.DD_LOGS.logger.error("Vinyl selection submit failed", {
            curatorId: myCuratorDocumentId,
            vinylCount: records.length,
            vinylIds: records.map((r) => r.id),
            error: {
              message: error.message || "Unknown error",
              stack: error.stack,
              response: error.response?.data,
              status: error.response?.status,
            },
            context: "vinyl_selection_submit",
            timestamp: new Date().toISOString(),
          });
        }

        addNotification(
          `Failed to submit vinyls: ${error.message || "Unknown error"}`,
          "error"
        );
        setSubmitted(false);
      });
  };

  useEffect(() => {
    if (
      topRecordsOf(shopCurator?.shopRecords).length === REQUIRED_VINYLS_COUNT
    ) {
      setSubmitted(true);
    }
  }, [shopCurator]);

  useEffect(() => {
    if (role === "admin" || role === "manager") return;
    if (!vinylListEnabled) {
      navigation("/");
    }
  }, [vinylListEnabled]);

  const tableEndRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!tableEndRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          observer.unobserve(target.target);
          fetchNextPage().finally(() => {
            if (hasNextPage) observer.observe(target.target);
          });
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );

    const currentRef = tableEndRef.current;
    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <WrapperContainer>
      <PageTopSection>
        <TopBarSection columnCount={3}>
          <TopBarColumn>
            <PageTitle>Soho Selections</PageTitle>
            <InstructionsContainer>
              <InstructionsText>
                <strong>Select </strong>,<strong>view </strong> and
                <strong> submit</strong>
                <br />
                your Curator Picks for{" "}
                <strong style={{ color: theme.colors.primary }}>
                  ROVR Soho.
                </strong>
              </InstructionsText>
              <SubmitButton
                style={{
                  cursor: submitted ? "not-allowed" : "pointer",
                }}
                disabled={!isReadyToSubmit || isLoading}
                submitted={submitted}
                onClick={handleSubmit}
              >
                {isLoading ? "Loading..." : submitted ? "Submitted" : "Submit"}
              </SubmitButton>
            </InstructionsContainer>
          </TopBarColumn>
          {isLoading || isFetchingSelected ? (
            <TopBarColumn style={SELECTION_COLUMN_STYLE}>
              <SelectedVinylWrapper
                style={{
                  gap: theme.margins.large,
                }}
              >
                {TOP_SLOT_KEYS.map((slot) => (
                  <VinylSquareIcon key={slot} />
                ))}
                {extrasEnabled && (
                  <ExtrasGroup>
                    <ExtrasLabel>Extras</ExtrasLabel>
                    <ExtrasSlots style={{ gap: theme.margins.large }}>
                      {EXTRA_SLOT_KEYS.map((slot) => (
                        <VinylSquareIcon key={slot} $dashed />
                      ))}
                    </ExtrasSlots>
                  </ExtrasGroup>
                )}
              </SelectedVinylWrapper>
            </TopBarColumn>
          ) : (
            <TopBarColumn style={SELECTION_COLUMN_STYLE}>
              <SelectedVinylWrapper style={{ gap: theme.margins.large }}>
                {TOP_SLOT_KEYS.map((slot) => (
                  <VinylCardSelected
                    key={slot}
                    onRemoveVinyl={
                      submitted ? undefined : () => onRemoveVinyl(slot)
                    }
                    vinyl={selectedVinyls[slot]}
                  />
                ))}
                {extrasEnabled && (
                  <ExtrasGroup>
                    <ExtrasLabel>Extras</ExtrasLabel>
                    <ExtrasSlots style={{ gap: theme.margins.large }}>
                      {EXTRA_SLOT_KEYS.map((slot) => (
                        <VinylCardSelected
                          key={slot}
                          dashed
                          onRemoveVinyl={
                            submitted ? undefined : () => onRemoveVinyl(slot)
                          }
                          vinyl={selectedVinyls[slot]}
                        />
                      ))}
                    </ExtrasSlots>
                  </ExtrasGroup>
                )}
              </SelectedVinylWrapper>
            </TopBarColumn>
          )}
        </TopBarSection>
      </PageTopSection>
      <ContentContainer contentContainerStyles={{ flexDirection: "column" }}>
        <div
          style={{
            width: "30%",
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: theme.margins.medium,
          }}
        >
          <SearchInput
            placeholder="Search vinyls"
            name="searchText"
            onChange={(e) => setFilters({ searchText: e.target.value })}
            value={filters.searchText}
          />
        </div>
        <VinylGridWrapper>
          {isLoading
            ? Array.from({ length: 24 }).map((_, idx) => (
                <VinylSkeleton key={idx + "loading"} />
              ))
            : vinylsData?.map((vinyl: any) => (
                <VinylCard
                  added={Object.values(selectedVinyls).some(
                    (v) => v?.id === vinyl.id
                  )}
                  onSelect={() => onSelectVinyl(vinyl)}
                  key={vinyl.id + vinyl?.name}
                  vinyl={vinyl}
                />
              ))}
          {isFetchingNextPage &&
            Array.from({ length: 24 }).map((_, idx) => (
              <VinylSkeleton key={idx + "fetching"} />
            ))}
          {tableEndRef && !isFetchingNextPage && (
            <div ref={tableEndRef} style={{ height: 1, width: "100%" }}></div>
          )}
        </VinylGridWrapper>
        {!isLoading && vinylsData?.length === 0 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              fontSize: "20px",
              color: theme.colors.gray,
              textAlign: "center",
              lineHeight: "2",
            }}
          >
            No vinyls found. <br />
            for the search term:{" "}
            <span style={{ fontWeight: "bold" }}>{filters.searchText}</span>
            {filters.searchText && (
              <>
                <span style={{ marginTop: theme.margins.small }}>
                  Try a different search term.
                </span>
                <UIButton
                  style={{
                    marginTop: theme.margins.small,
                    fontSize: "1rem",
                    width: "200px",
                  }}
                  variant="primaryOutline"
                  onClick={() => setFilters({ searchText: "" })}
                >
                  Remove filter
                </UIButton>
              </>
            )}
          </div>
        )}
        <UIModal
          modalStyles={{
            backgroundColor: theme.colors.primary,
            color: theme.colors.black,
            maxWidth: "538px",
            minHeight: "261px",
            justifyContent: "space-evenly",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
          }}
          isOpen={reachLimitAlertIsOpen}
          title="You’ve reached the limit of selectable vinyls."
          headerStyles={{
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            marginBottom: 0,
          }}
          titleStyles={{ width: "60%", textAlign: "center", fontSize: "27px" }}
          onClose={closeReachLimitAlert}
          actions={[
            <UIButton
              style={{
                width: "78px",
                height: "33px",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
              variant="darkOutline"
              onClick={closeReachLimitAlert}
            >
              Ok
            </UIButton>,
          ]}
        >
          <div>
            If you’d like to add this one, please remove a vinyl you previously
            chose.
          </div>
        </UIModal>
      </ContentContainer>
    </WrapperContainer>
  );
};

/**
 * The seven slots (top 5 + 2 extras) are wider than one of the header's three
 * columns, so the selection spans the empty third one instead of being clipped
 * by the header's `overflow: hidden`.
 */
const SELECTION_COLUMN_STYLE: React.CSSProperties = {
  overflow: "visible",
  gridColumn: "span 2",
};

const VinylGridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(213px, 0fr));
  width: 100%;
  margin: 0;
  padding: 0;
  gap: 0;
  overflow-y: auto;
  overflow-x: hidden;
  justify-content: center;
  flex: 1;
  min-height: 0;
`;
const SelectedVinylWrapper = styled.div`
  display: flex;
`;
export const VinylSquareIcon = styled.div<{ $dashed?: boolean }>`
  width: 100px;
  height: 100px;
  background: ${theme.colors.gray400};
  border-radius: 5px;
  border: ${(props) =>
    props.$dashed
      ? `1px dashed ${theme.colors.gray}`
      : `1px solid ${theme.colors.gray300}`};
`;

/**
 * The two "extra" picks, set apart from the top 5 by a dotted rule and dotted
 * slot outlines so they never read as part of the list [ROVR-4187].
 */
const ExtrasGroup = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  margin-left: ${theme.margins.small};
  padding-left: ${theme.margins.medium};
  border-left: 1px dashed ${theme.colors.gray};
`;
const ExtrasLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.gray};
`;
const ExtrasSlots = styled.div`
  display: flex;
`;

const InstructionsContainer = styled.div`
  display: flex;
  gap: ${theme.margins.small};
  font-size: 15px;
  justify-content: center;
  align-items: center;
`;
const InstructionsText = styled.div`
  font-size: 15px;
  color: ${theme.colors.gray};
`;
export default VinylPage;
